import {FaCircleCheck} from "react-icons/fa6";

import {Icon} from "@/components/icon/Icon.tsx";
import {MarketingFAQs} from "@/marketingPages/components/marketingfaqs/MarketingFAQs.tsx";

interface ThirdPartyAccordionProps {
  serviceName: string;
}

export const ThirdPartyAccordion = ({serviceName}: ThirdPartyAccordionProps) => {
  const items = [
    {
      question: (
        <div className="marketingTextS3Bold pulse-color-gray-800 d-flex align-items-center gap-2">
          <Icon icon={FaCircleCheck} className="pulse-color-green-500" size="marketing" />
          <div className="text-start">Down Notifications</div>
        </div>
      ),
      answer: `Receive real-time alerts from Uptime.com when ${serviceName} experiences an outage. These automatic notifications help your team respond proactively to external disruptions that could impact your application’s performance or availability, minimizing user-facing issues and improving incident response times.`,
    },
    {
      question: (
        <div className="marketingTextS3Bold pulse-color-gray-800 d-flex align-items-center gap-2">
          <Icon icon={FaCircleCheck} className="pulse-color-green-500" size="marketing" />
          <div className="text-start">Warning Notifications</div>
        </div>
      ),
      answer: `Stay informed of partial outages or degraded performance from ${serviceName} with warning-level alerts from Uptime.com. These notifications provide early indicators of potential issues with integrated third-party services, allowing your team to investigate and mitigate risk before a complete service failure occurs.`,
    },
    {
      question: (
        <div className="marketingTextS3Bold pulse-color-gray-800 d-flex align-items-center gap-2">
          <Icon icon={FaCircleCheck} className="pulse-color-green-500" size="marketing" />
          <div className="text-start">Maintenance Notifications</div>
        </div>
      ),
      answer: `Uptime.com will automatically alert you when ${serviceName} schedules or enters maintenance windows. Knowing about these planned service interruptions in advance enables better planning and communication with stakeholders and customers, reducing confusion and support inquiries during downtime.`,
    },
    {
      question: (
        <div className="marketingTextS3Bold pulse-color-gray-800 d-flex align-items-center gap-2">
          <Icon icon={FaCircleCheck} className="pulse-color-green-500" size="marketing" />
          <div className="text-start">Status Messages</div>
        </div>
      ),
      answer: `Uptime.com monitors and collects status messages directly from ${serviceName}, whether it’s an outage, incident resolution update, or performance notice, and notifies your team of status changes.`,
    },
    {
      question: (
        <div className="marketingTextS3Bold pulse-color-gray-800 d-flex align-items-center gap-2">
          <Icon icon={FaCircleCheck} className="pulse-color-green-500" size="marketing" />
          <div className="text-start">Status Details</div>
        </div>
      ),
      answer: `Uptime.com collects status update details from ${serviceName} so you can monitor and understand how issues are being handled. Get status updates from ${serviceName} on how they are mitigating incidents, and other details from their team.`,
    },
    {
      question: (
        <div className="marketingTextS3Bold pulse-color-gray-800 d-flex align-items-center gap-2">
          <Icon icon={FaCircleCheck} className="pulse-color-green-500" size="marketing" />
          <div className="text-start">Component Status Filtering</div>
        </div>
      ),
      answer: `Track and filter specific components of ${serviceName} services that are most critical to your business. Uptime.com monitors multiple components offered by ${serviceName} that have individual statuses to ensure you’re monitoring the elements that matter most, differentiating when different services, components, or regions are down.`,
    },
  ];

  return (
    <MarketingFAQs
      items={items}
      title="How Third Party Monitoring Works"
      description={`Uptime.com’s Third Party Monitoring gives you full visibility into the health and performance of the external services your business depends on—like ${serviceName}, and other cloud platforms. With real-time alerts, detailed status insights, and component-level tracking, Uptime.com takes the guesswork out of third party service monitoring by giving you actionable insights into the performance of your vendors.`}
    />
  );
};
